/**
 * Колокольчик в шапке: счётчик непрочитанных из `MeDto.unread_notifications`,
 * выпадающий список `GET /api/notifications` с курсором `before`/`next_before`/`has_more`
 * и кнопка «Прочитано» (`POST /api/notifications/read`).
 *
 * Маркер прочитанного один на систему (`users.notify_prefs.bell_read_id`), поэтому значок
 * в шапке и число в списке всегда совпадают.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import { useMarkNotificationsRead, useNotificationsInfinite } from '../api/hooks';
import { useAuth } from '../auth/AuthContext';
import { fmtDateTime } from '../lib/format';
import { ErrorState, LoadingState } from './ErrorState';
import { useOpenCase } from './Modal';
import type { NotificationDto } from '../api/types';

export function NotificationBell() {
    const { unreadNotifications } = useAuth();
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement | null>(null);
    const openCase = useOpenCase();

    const query = useNotificationsInfinite(20, { enabled: open });
    const markRead = useMarkNotificationsRead();

    const items = useMemo<NotificationDto[]>(
        () => (query.data ? query.data.pages.flatMap((page) => page.items) : []),
        [query.data],
    );
    const firstPage = query.data?.pages[0];
    const unread = firstPage ? firstPage.unread : unreadNotifications;

    useEffect(() => {
        if (!open) return;
        const onPointerDown = (event: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) setOpen(false);
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    const handleRead = () => {
        const newest = items.length > 0 ? items[0].id : undefined;
        markRead.mutate(newest === undefined ? {} : { up_to_id: newest });
    };

    const handleItem = (item: NotificationDto) => {
        setOpen(false);
        if (item.case_id) openCase(item.case_id);
    };

    return (
        <div className="bell" ref={wrapRef}>
            <button
                type="button"
                className="bell-btn"
                aria-label={unread > 0 ? 'Уведомления, непрочитанных: ' + unread : 'Уведомления'}
                aria-expanded={open}
                onClick={() => setOpen((prev) => !prev)}
            >
                <span aria-hidden="true">🔔</span>
                {unread > 0 && <span className="bell-count">{unread > 99 ? '99+' : unread}</span>}
            </button>

            {open && (
                <div className="bell-pop" role="dialog" aria-label="Уведомления">
                    <div className="bell-head">
                        <span className="bell-title">Уведомления</span>
                        <button
                            type="button"
                            className="link-btn"
                            onClick={handleRead}
                            disabled={markRead.isPending || unread === 0}
                        >
                            Прочитано
                        </button>
                    </div>

                    <div className="bell-list">
                        {query.isPending && <LoadingState variant="inline" />}
                        {query.isError && <ErrorState variant="inline" error={query.error} />}
                        {!query.isPending && !query.isError && items.length === 0 && (
                            <div className="bell-empty">Уведомлений пока нет.</div>
                        )}
                        {items.map((item, index) => (
                            <button
                                key={item.id}
                                type="button"
                                className={index < unread ? 'bell-item unread' : 'bell-item'}
                                onClick={() => handleItem(item)}
                            >
                                <span className="bell-text">{item.text}</span>
                                <span className="bell-time">
                                    {fmtDateTime(item.created_at)}
                                    {item.case_number ? ' · ' + item.case_number : ''}
                                </span>
                            </button>
                        ))}
                    </div>

                    {query.hasNextPage && (
                        <button
                            type="button"
                            className="bell-more"
                            onClick={() => void query.fetchNextPage()}
                            disabled={query.isFetchingNextPage}
                        >
                            {query.isFetchingNextPage ? 'Загружаем…' : 'Показать ещё'}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export default NotificationBell;
